import { useEffect, useRef, useState } from 'react';

export default function CommunitySection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const values = [
    { label: 'Learning Together', icon: 'ri-book-open-line' },
    { label: 'Intentional Living', icon: 'ri-seedling-line' },
    { label: 'Practicing Stewardship', icon: 'ri-heart-pulse-line' },
    { label: 'Building Peaceful Futures', icon: 'ri-home-smile-line' },
  ];

  return (
    <section ref={ref} className="bg-sage-200/40 py-24 md:py-36 relative overflow-hidden">
      {/* Decorative shapes */}
      <div className="absolute top-0 left-1/4 w-80 h-80 bg-sage-100/50 rounded-full blur-[100px]" />
      <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-cream-200/30 rounded-full blur-[80px]" />

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div
            className={`text-center mb-16 md:mb-24 transition-all duration-1000 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-sage-500 text-xs tracking-[0.3em] uppercase mb-4 font-medium">
              The Movement
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-charcoal-300 font-medium leading-tight max-w-3xl mx-auto">
              You are not behind.
              <br />
              <span className="italic font-light">You were never taught to practice.</span>
            </h2>
          </div>

          {/* Values Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mb-16 md:mb-24">
            {values.map((v, i) => (
              <div
                key={v.label}
                className={`bg-white/70 backdrop-blur-sm rounded-2xl p-7 md:p-8 text-center transition-all duration-700 hover:bg-white hover:shadow-[0_4px_20px_rgba(0,0,0,0.03)] ${
                  visible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${200 + i * 120}ms` }}
              >
                <div className="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center mx-auto mb-5">
                  <i className={`${v.icon} text-sage-500 text-lg`} />
                </div>
                <h4 className="text-charcoal-300 text-sm md:text-base font-medium">
                  {v.label}
                </h4>
              </div>
            ))}
          </div>

          {/* Image + Quote */}
          <div
            className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center transition-all duration-1000 delay-500 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=A%20peaceful%20community%20gathering%20scene%20soft%20warm%20natural%20lighting%20Filipino%20family%20atmosphere%20gentle%20beige%20and%20earth%20tones%20warm%20white%20background%20calm%20serene%20mood%20people%20in%20soft%20focus%20editorial%20photography%20style%20muted%20colors%20intentional%20living%20aesthetic%20no%20clutter&width=600&height=450&seq=community1&orientation=landscape"
                alt="Peaceful community gathering"
                className="w-full h-64 md:h-80 object-cover object-center rounded-2xl"
              />
            </div>
            <div>
              <p className="font-serif text-xl md:text-2xl text-charcoal-300 italic leading-relaxed mb-6">
                Stewardship is not a skill you learn once. It is a practice you choose every day.
              </p>
              <p className="text-charcoal-100 text-sm leading-relaxed">
                This is a movement of people choosing intentionality over urgency. People who believe that how we handle money reveals what we truly value. We are building a community where financial peace is not the exception — it is the practice.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}